import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import env from './utils/env';
import Loader from './components/Loader';
import PageInfo from './components/PageInfo';
import {
    ISearchTarget,
    SearchTargetsListResponse,
    formatDate,
    truncateText,
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS
} from './types/adminDebug';
import './ReportSourcesView.css';

const AdminSearchTargetsView: React.FC = () => {
    const { token, authLoaded } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // URL state
    const currentPage = parseInt(searchParams.get('page') || '1', 10);
    const searchQuery = searchParams.get('search') || '';
    const filterCountry = searchParams.get('country') || '';
    const filterCategory = searchParams.get('category') || '';
    const sortBy = searchParams.get('sortBy') || 'search_timestamp';
    const sortOrder = (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc';
    const pageSize = parseInt(searchParams.get('limit') || String(DEFAULT_PAGE_SIZE), 10);

    // Local state
    const [searchTargets, setSearchTargets] = useState<ISearchTarget[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [filterOptions, setFilterOptions] = useState<{ countries: string[]; categories: string[] }>({
        countries: [],
        categories: []
    });

    const apiBaseUrl = env.apiUrl;
    const apiKey = env.apiKey;

    // Update URL params helper
    const updateSearchParams = useCallback((updates: Record<string, string | null>) => {
        const newParams = new URLSearchParams(searchParams);
        Object.entries(updates).forEach(([key, value]) => {
            if (value === null || value === '') {
                newParams.delete(key);
            } else {
                newParams.set(key, value);
            }
        });
        setSearchParams(newParams);
    }, [searchParams, setSearchParams]);

    // Fetch filter options
    const fetchFilterOptions = useCallback(async () => {
        try {
            const headers: Record<string, string> = {
                'x-api-key': apiKey,
                'Content-Type': 'application/json'
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${apiBaseUrl}/search-targets/filters/options`, {
                headers
            });

            if (response.ok) {
                const data = await response.json();
                setFilterOptions({
                    countries: data.countries || [],
                    categories: data.categories || []
                });
            }
        } catch (err) {
            console.error('Failed to fetch filter options:', err);
        }
    }, [apiBaseUrl, apiKey, token]);

    // Fetch search targets
    const fetchSearchTargets = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({
                page: currentPage.toString(),
                limit: pageSize.toString(),
                sortBy,
                sortOrder
            });

            if (searchQuery) params.append('search', searchQuery);
            if (filterCountry) params.append('country', filterCountry);
            if (filterCategory) params.append('category', filterCategory);

            const headers: Record<string, string> = {
                'x-api-key': apiKey,
                'Content-Type': 'application/json'
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${apiBaseUrl}/search-targets?${params}`, {
                headers
            });

            if (!response.ok) {
                const errText = await response.text();
                throw new Error('Failed to fetch search targets: ' + (response.statusText || errText || 'Unknown error'));
            }

            const data: SearchTargetsListResponse = await response.json();
            setSearchTargets(data.searchTargets);
            setTotalPages(data.pagination.totalPages);
            setTotalItems(data.pagination.total);
        } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, searchQuery, filterCountry, filterCategory, sortBy, sortOrder, token, apiBaseUrl, apiKey]);

    useEffect(() => {
        if (authLoaded) {
            fetchFilterOptions();
        }
    }, [authLoaded, fetchFilterOptions]);

    useEffect(() => {
        if (authLoaded) {
            fetchSearchTargets();
        }
    }, [authLoaded, fetchSearchTargets]);

    const handlePageChange = (page: number) => {
        updateSearchParams({ page: page.toString() });
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateSearchParams({ search: e.target.value || null, page: '1' });
    };

    const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        updateSearchParams({ country: e.target.value || null, page: '1' });
    };

    const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        updateSearchParams({ category: e.target.value || null, page: '1' });
    };

    const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        updateSearchParams({ limit: e.target.value, page: '1' });
    };

    const handleSort = (column: string) => {
        const newOrder = sortBy === column && sortOrder === 'desc' ? 'asc' : 'desc';
        updateSearchParams({ sortBy: column, sortOrder: newOrder });
    };

    const handleClearFilters = () => {
        updateSearchParams({
            search: null,
            country: null,
            category: null,
            sortBy: null,
            sortOrder: null,
            page: '1'
        });
    };

    const handleRowClick = (target: ISearchTarget) => {
        navigate(`/admin/search-targets/${target._id}`);
    };

    const getSortIndicator = (column: string) => {
        if (sortBy !== column) return null;
        return sortOrder === 'asc' ? ' ▲' : ' ▼';
    };

    // Show loading while auth is loading
    if (!authLoaded) {
        return <Loader text={t('common.loading')} />;
    }

    const hasActiveFilters = searchQuery || filterCountry || filterCategory;

    return (
        <div className="container">
            <h1>{t('adminDebug.searchTargets.title')}</h1>
            <p className="page-description">{t('adminDebug.searchTargets.description')}</p>

            {/* Filters */}
            <div className="filters-container" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                    type="text"
                    placeholder={t('adminDebug.searchTargets.searchPlaceholder')}
                    value={searchQuery}
                    onChange={handleSearchChange}
                    style={{ padding: '0.5rem', minWidth: '200px', flex: '1' }}
                />
                <select
                    value={filterCountry}
                    onChange={handleCountryChange}
                    style={{ padding: '0.5rem' }}
                >
                    <option value="">{t('adminDebug.filters.allCountries')}</option>
                    {filterOptions.countries.map(country => (
                        <option key={country} value={country}>{country}</option>
                    ))}
                </select>
                <select
                    value={filterCategory}
                    onChange={handleCategoryChange}
                    style={{ padding: '0.5rem' }}
                >
                    <option value="">{t('adminDebug.filters.allCategories')}</option>
                    {filterOptions.categories.map(category => (
                        <option key={category} value={category}>{category}</option>
                    ))}
                </select>
                <select
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    style={{ padding: '0.5rem' }}
                >
                    {PAGE_SIZE_OPTIONS.map(size => (
                        <option key={size} value={size}>{size} {t('adminDebug.perPage')}</option>
                    ))}
                </select>
                {hasActiveFilters && (
                    <button
                        onClick={handleClearFilters}
                        style={{ padding: '0.5rem 1rem', cursor: 'pointer' }}
                    >
                        {t('adminDebug.filters.clear')}
                    </button>
                )}
            </div>

            {error && (
                <div className="error-message" style={{ color: 'red', marginBottom: '1rem' }}>
                    {error}
                </div>
            )}

            {loading ? (
                <Loader text={t('adminDebug.searchTargets.loading')} />
            ) : searchTargets.length > 0 ? (
                <div className="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th
                                    onClick={() => handleSort('_id')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {t('adminDebug.fields.id')}{getSortIndicator('_id')}
                                </th>
                                <th
                                    onClick={() => handleSort('query')}
                                    style={{ cursor: 'pointer', width: '300px' }}
                                >
                                    {t('adminDebug.fields.query')}{getSortIndicator('query')}
                                </th>
                                <th
                                    onClick={() => handleSort('issuer')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {t('adminDebug.fields.issuer')}{getSortIndicator('issuer')}
                                </th>
                                <th
                                    onClick={() => handleSort('category')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {t('adminDebug.fields.category')}{getSortIndicator('category')}
                                </th>
                                <th
                                    onClick={() => handleSort('country')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {t('adminDebug.fields.country')}{getSortIndicator('country')}
                                </th>
                                <th
                                    onClick={() => handleSort('search_timestamp')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {t('adminDebug.fields.searchTimestamp')}{getSortIndicator('search_timestamp')}
                                </th>
                                <th
                                    onClick={() => handleSort('number_of_results')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {t('adminDebug.fields.numberOfResults')}{getSortIndicator('number_of_results')}
                                </th>
                                <th
                                    onClick={() => handleSort('source')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {t('adminDebug.fields.source')}{getSortIndicator('source')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {searchTargets.map(target => (
                                <tr
                                    key={target._id}
                                    onClick={() => handleRowClick(target)}
                                    style={{ cursor: 'pointer' }}
                                    className="clickable-row"
                                >
                                    <td title={target._id}>{truncateText(target._id, 12)}</td>
                                    <td title={target.query}>{truncateText(target.query, 50)}</td>
                                    <td title={target.issuer}>{truncateText(target.issuer, 20)}</td>
                                    <td>{target.category || '-'}</td>
                                    <td>{target.country || '-'}</td>
                                    <td>{formatDate(target.search_timestamp)}</td>
                                    <td>{target.number_of_results ?? '-'}</td>
                                    <td>{target.source || '-'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {/* Page Info */}
                    <PageInfo
                        count={searchTargets.length}
                        total={totalItems}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        label={t('adminDebug.searchTargets.label')}
                    />

                    {/* Pagination Controls */}
                    <div className="pagination-controls">
                        <button
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage <= 1}
                            className="pagination-button"
                        >
                            <i className="fas fa-angle-double-left"></i>
                        </button>

                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage <= 1}
                            className="pagination-button"
                        >
                            <i className="fas fa-angle-left"></i>
                        </button>

                        <span className="pagination-page-info">
                            {t('adminDebug.pagination.page')} {currentPage} {t('adminDebug.pagination.of')} {totalPages}
                        </span>

                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage >= totalPages}
                            className="pagination-button"
                        >
                            <i className="fas fa-angle-right"></i>
                        </button>

                        <button
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage >= totalPages}
                            className="pagination-button"
                        >
                            <i className="fas fa-angle-double-right"></i>
                        </button>
                    </div>
                </div>
            ) : (
                <div className="no-results">{t('adminDebug.searchTargets.noResults')}</div>
            )}
        </div>
    );
};

export default AdminSearchTargetsView;
